
import { computed, defineComponent, PropType, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ContentAdaptLayout from '../../../../_components/content-adapt-layout/content-adapt-layout.vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  GroupPolicySettingsService,
  GroupPolicyTypes,
  LeakFixSettings,
  setDefaultRepairData,
  OsOption
} from '../../group-policy-settings.service'
import MixSelectors from '@/views/dashboard/_components/group-policy-settings/_components/mix-selectors/mix-selectors.vue'
import { useRoute, useRouter } from 'vue-router'
import { OperateType } from '@/views/dashboard/_components/group-policy-settings/group-policy-drawer.service'
import DetailsSubscribeBar from '@/views/dashboard/_components/subscribe-bar/details-subscribe-bar.vue'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import { PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'
const DEFAULT_PARAMS = {
  scan_frequency: 1,
  frequency: 1,
  restart_remind_type: 2,
  restart_remind_interval_min: 10,
  blue_screen_repair_switch: false,
  repair_task_main_switch: false,
  repair_os_list: [] as string[],
  warn_level_list: [0, 1]
}
const IntervalMinOptions = [
  { value: 5 },
  { value: 10 },
  { value: 15 },
  { value: 20 },
  { value: 25 },
  { value: 30 },
  { value: 35 },
  { value: 40 },
  { value: 45 },
  { value: 50 },
  { value: 55 },
]

export default defineComponent({
  components: {
    DetailsSubscribeBar,
    ContentAdaptLayout,
    MixSelectors
  },
  props: {
    operateType: {
      type: String as PropType<'edit'|'add'|'read'>,
      default: 'edit'
    },
    policyId: {
      type: String,
      default: undefined
    },
    name: {
      type: String,
      default: ''
    },
    activeName: {
      type: String,
      default: ''
    },
    systemTab: {
      type: String,
      default: '1'
    }
  },
  setup (props, context) {
    const { t } = useI18n()
    const route = useRoute()
    const router = useRouter()
    const injector = useReflectiveInjector()
    const servicePermissionService = injector.get(ServicePermissionService)
    const groupPolicySettingsService = injector.get(GroupPolicySettingsService)

    const loading = ref<boolean>(true)
    const systemOptions = reactive({
      options: [] as OsOption[]
    })
    const strategy = reactive<LeakFixSettings>({
      ...DEFAULT_PARAMS
    })
    const data = reactive({
      frequency: 1,
      scan_frequency: 1,
      hasEdit: false
    })

    const hasPermission = computed(() => {
      if (+props.systemTab === TerminalType.linux) {
        return servicePermissionService.getHasPermission(PermissionFe.linux_base_auth)
      }
      return true
    })

    function getSystems () {
      return groupPolicySettingsService.getLeakRepairSystems().then(res => {
        systemOptions.options = res.list
      })
    }
    function getSettings () {
      getSystems().then(() => {
        groupPolicySettingsService.getSettings({
          id: props.policyId,
          type: props.systemTab === '1' ? GroupPolicyTypes.LeakFix : GroupPolicyTypes.LinuxLeakFix
        }).then(response => {
          const copySt = setDefaultRepairData(response.strategy, systemOptions.options)
          groupPolicySettingsService.updateSelectors('leakScanSelectors', (response.strategy as LeakFixSettings).scan_tasks)
          if (props.systemTab === '1') {
            groupPolicySettingsService.updateSelectors('leakRepairSelectors', (response.strategy as LeakFixSettings).regular_tasks)
          }
          Object.assign(strategy, {
            ...DEFAULT_PARAMS,
          }, copySt)
          loading.value = false
        }).finally(() => {
          loading.value = false
        })
      })
    }
    function saveSettings () {
      const commitStrategy: LeakFixSettings = Object.assign({}, strategy, {
        // 如果选了高危要拼1，2，3，4，5，6传给后端
        // eslint-disable-next-line no-magic-numbers
        warn_level_list: strategy.warn_level_list.includes(1) ? [...strategy.warn_level_list, ...[2, 3, 4, 5, 6]] : strategy.warn_level_list
      })
      groupPolicySettingsService.saveSettings({
        id: props.policyId,
        type: props.systemTab === '1' ? GroupPolicyTypes.LeakFix : GroupPolicyTypes.LinuxLeakFix,
        strategy: JSON.stringify(commitStrategy)
      }).then(() => {
        groupPolicySettingsService.updateEditTab(false)
        context.emit('save')
      })
    }
    function setChangeStatus () {
      groupPolicySettingsService.updateEditTab(true)
    }
    function cancel () {
      context.emit('cancel')
    }
    groupPolicySettingsService.leakScanSelectors.subscribe((res: any) => {
      strategy.scan_tasks = res
    })
    groupPolicySettingsService.leakRepairSelectors.subscribe((res: any) => {
      strategy.regular_tasks = res
    })
    groupPolicySettingsService.hasEditTab.subscribe((hasEdit: boolean) => {
      data.hasEdit = hasEdit
    })
    groupPolicySettingsService.saveTab.subscribe((name: string) => {
      if (name === 'leak-fix') {
        saveSettings()
      }
    })
    watch(() => props.systemTab, (newV) => {
      getSettings()
    })
    watch(() => props.activeName, (newV) => {
      if (newV === 'leak-fix') {
        getSettings()
      }
    })
    // getSystems()
    getSettings()
    return {
      t,
      IS_JP_ONLY_BUILD,
      TerminalType,
      PermissionFe,
      OperateType,
      data,
      loading,
      hasPermission,
      strategy,
      IntervalMinOptions,
      systemOptions,
      setChangeStatus,
      saveLeakSetting () {
        saveSettings()
      },
      jumpLeak () {
        router.push({
          name: 'vulnerability-fix',
          query: {
            ...route.query,
            tab: 'ignore-view',
          }
        })
      },
      changeScanFrequency () {
        setChangeStatus()
        groupPolicySettingsService.updateSelectors('leakScanSelectors', [{ target: 1, start_min: 0, end_min: 10 }])
      },
      changeFrequency () {
        setChangeStatus()
        groupPolicySettingsService.updateSelectors('leakRepairSelectors', [{ target: 1, start_min: 0, end_min: 10 }])
      },
      cancel,
    }
  }
})
